import { useLocation } from "react-router-dom";
import SEO from "../../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import PhysicalAIDatabasePanel from "../../components/physical-ai/PhysicalAIDatabasePanel";
import { getPhysicalAIIntelligencePageByPath } from "../../data/physicalAIIntelligence";
import { seoConfig } from "../../data/seoConfig";

function buildSchemas(page) {
  const url = `${seoConfig.baseUrl}${page.path}`;
  const breadcrumbs = [
    { label: "Home", to: "/" },
    ...(page.path.startsWith("/data/")
      ? [{ label: "Data", to: "/data" }]
      : [{ label: "Physical AI", to: "/physical-ai" }]),
    { label: page.title, to: page.path },
  ];
  return {
    breadcrumbs,
    schemas: [
      buildBreadcrumbSchema(breadcrumbs),
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: page.title,
        description: page.description,
        url,
        dateModified: page.updated,
        isPartOf: { "@type": "WebSite", name: "TechniaHQRobot", url: seoConfig.baseUrl },
      },
      {
        "@context": "https://schema.org",
        "@type": "Dataset",
        name: page.title,
        description: page.description,
        url,
        dateModified: page.updated,
        creator: { "@type": "Organization", name: "TechniaHQRobot", url: seoConfig.baseUrl },
        isAccessibleForFree: true,
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${page.title} records`,
        numberOfItems: page.records.length,
        itemListElement: page.records.slice(0, 50).map((record, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: String(record.model || record.dataset || record.benchmark || record.robot || record.id),
        })),
      },
    ],
  };
}

export default function PhysicalAIIntelligencePage() {
  const location = useLocation();
  const page = getPhysicalAIIntelligencePageByPath(location.pathname);
  if (!page || page.existing) return null;

  const { breadcrumbs, schemas } = buildSchemas(page);

  return (
    <>
      <SEO
        title={`${page.seoTitle} TechniaHQRobot`}
        description={page.description}
        path={page.path}
        modifiedTime={page.updated}
        schema={schemas}
      />
      <div className="bg-[#030712] text-slate-100">
        <div className="border-b border-white/10"><Breadcrumbs items={breadcrumbs} /></div>
        <div className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" style={{ backgroundImage: "linear-gradient(rgba(56,189,248,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.05) 1px,transparent 1px)", backgroundSize: "38px 38px" }} />
          <div className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-blue-600/10 blur-3xl" aria-hidden="true" />

          <section className="site-shell relative py-10 sm:py-14">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">{page.eyebrow}</p>
            <h1 className="mt-4 max-w-5xl text-4xl font-black tracking-[-0.04em] text-white sm:text-6xl">{page.title}</h1>
            <p className="mt-5 max-w-4xl text-base leading-8 text-slate-300 sm:text-lg">{page.description}</p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs font-bold text-slate-500">
              <span>{page.records.length} retained records</span>
              <span>{page.verificationLabel || "Source dates are recorded with the data; this is not a live feed"}</span>
              <span>Primary-source fields preserved per record</span>
            </div>
          </section>

          <div className="site-shell relative pb-20">
            <PhysicalAIDatabasePanel page={page} />
            <section className="mt-8 grid gap-4 border-t border-white/10 pt-8 md:grid-cols-3">
              <div><p className="text-xs font-black uppercase tracking-[0.15em] text-cyan-300">Included</p><p className="mt-2 text-sm leading-7 text-slate-400">Published model, robot, benchmark, deployment or failure evidence that can be tied to a retained source.</p></div>
              <div><p className="text-xs font-black uppercase tracking-[0.15em] text-cyan-300">Not inferred</p><p className="mt-2 text-sm leading-7 text-slate-400">Unknown autonomy, latency, trial counts, deployment scale and failure causes remain unknown when the source does not disclose them.</p></div>
              <div><p className="text-xs font-black uppercase tracking-[0.15em] text-cyan-300">Update policy</p><p className="mt-2 text-sm leading-7 text-slate-400">Versions and historical records remain distinguishable. A new release does not silently overwrite an older model or evaluation condition.</p></div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
